import RubricSettingsSection from 'components/CreateQuiz/RubricSettingsSection';
import { useApp } from 'contexts/app';
import { PageContext } from 'pages/CreateQuiz/PageProvider';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Container, Divider, Stack, Switch, Text } from 'shell/components';
import { isQuizFeedbackSettingsDefault } from 'utils/quizFeedback';
import { hasQuizRubric } from 'utils/quizRubric';

const FeedbackSettingsStep = ({ device }) => {
    const { t } = useTranslation(['quiz', 'assignment']);
    const { isFeedbackFeatureDisabled } = useApp();
    const { form, setForm, setFeedbackDialogOpen } = useContext(PageContext);
    const isMobile = device === 'mobile';

    return (
        <Container
            sx={(theme) => ({
                flex: 1,
                maxWidth: 'none',
                minHeight: 0,
                px: device === 'desktop' ? 8 : 4,
                py: 5,
                background: theme.palette.ensightful.shades[0],
            })}
            device={device}
            maxWidth={false}
        >
            <Stack spacing={5} sx={{ width: '100%' }}>
                <Stack spacing={2}>
                    <Text component={'h2'} variant={'sh1'} weight={'medium'} sx={{ fontSize: '1.25rem' }}>
                        {t('settings.title', 'Grading & Feedback Settings')}
                    </Text>
                    <Text sx={{ maxWidth: '600px' }}>
                        {t(
                            hasQuizRubric(form.rubric) ? 'settings.description' : 'settings.import-description',
                            hasQuizRubric(form.rubric)
                                ? 'Use TimelyGrader for providing grading and feedback, explaining the reasoning for each grade.'
                                : 'Import the rubric from your LMS and use TimelyGrader for providing grading and feedback, explaining the reasoning for each grade.'
                        )}
                    </Text>
                </Stack>
                <Divider />
                <RubricSettingsSection device={device} />
                <Stack
                    sx={(theme) => ({
                        border: `1px solid ${theme.palette.ensightful.neutral[300]}`,
                        borderRadius: '16px',
                        p: 3,
                    })}
                    spacing={4}
                >
                    <Stack
                        direction={'row'}
                        alignItems={isMobile ? 'flex-start' : 'center'}
                        justifyContent={'space-between'}
                        spacing={3}
                    >
                        <Stack
                            sx={{ minWidth: 0 }}
                            direction={isMobile ? 'column' : 'row'}
                            alignItems={isMobile ? 'flex-start' : 'center'}
                            spacing={3}
                        >
                            <Text
                                component={'label'}
                                id={'quiz-feedback-label'}
                                htmlFor={'quiz-feedback-toggle'}
                                variant={'p1'}
                                weight={'medium'}
                            >
                                {t('feedback.include', 'Include Quiz Feedback')}
                            </Text>
                            <Button
                                variant={'borderless'}
                                disabled={!form.aiFeedbackEnabled || isFeedbackFeatureDisabled}
                                onClick={() => setFeedbackDialogOpen(true)}
                            >
                                {t('assignment:advanced-settings', 'Advanced Settings')}
                            </Button>
                        </Stack>
                        <Switch
                            sx={{ flexShrink: 0 }}
                            id={'quiz-feedback-toggle'}
                            aria-labelledby={'quiz-feedback-label'}
                            checked={form.aiFeedbackEnabled && !isFeedbackFeatureDisabled}
                            disabled={isFeedbackFeatureDisabled}
                            tooltip={
                                isFeedbackFeatureDisabled
                                    ? {
                                          component: t(
                                              'assignment:institution-disabled',
                                              'Disabled by your institution. Request access or contact IT.'
                                          ),
                                      }
                                    : undefined
                            }
                            onChange={(_, checked) =>
                                setForm((current) => ({ ...current, aiFeedbackEnabled: checked }))
                            }
                        />
                    </Stack>
                    <Text sx={{ maxWidth: '600px' }}>
                        {t(
                            'feedback.description',
                            'When turned on, TimelyGrader generates question-specific feedback that instructors can share with students to explain the reasoning behind each grade.'
                        )}
                    </Text>
                    <Text color={'light'}>
                        {isQuizFeedbackSettingsDefault(form.feedbackSettings, form.feedbackCustomContext)
                            ? t('assignment:system-default', 'System Default')
                            : t('assignment:edited', 'Edited')}
                    </Text>
                </Stack>
            </Stack>
        </Container>
    );
};

export default FeedbackSettingsStep;
